/// <amd-module name="Build/Common/Library/Legacy/ObservablePivotActionArray" />
import { ObservableArray } from 'VSS/Core/Observable';
import { IPivotBarAction } from 'VSSUI/PivotBar';
import { ContributedItemArray, ContributedItemWithIdArray } from 'VSSUI/Utilities/ItemContribution';
import { IHeaderCommandBarItem } from 'VSSUI/HeaderCommandBar';
import { MenuItemType, IMenuItem, IMenuProps, IMenu } from "VSSUI/Menu";
import { ContributedPivotBarActionProvider, PivotActionContextType } from "VSS/Features/Frame/ContributedPivotBarActionProvider";
import { IVssPageContext } from 'VSS/Platform/Context';

export enum ItemOrdering {
    StaticFirst = 0,
    ContributedFirst = 1
}

export type MutateCallback = (item: IHeaderCommandBarItem) => IHeaderCommandBarItem;

export interface IHeaderCommandContribution {
    contributionId: string,
    getContributionContext: PivotActionContextType | undefined
}

export class ObservableHeaderCommandArray extends ObservableArray<IHeaderCommandBarItem> {
    private _staticItems: IHeaderCommandBarItem[];
    private _contributedItems: ContributedItemArray<IPivotBarAction, IPivotBarAction>[];
    private _divider: IHeaderCommandBarItem | undefined;
    private _order: ItemOrdering;
    private _mutate: MutateCallback | undefined;

    constructor( pageContext: IVssPageContext,
        staticItems: IHeaderCommandBarItem[],
        contributions: IHeaderCommandContribution[],
        order: ItemOrdering,
        divider?: IHeaderCommandBarItem,
        mutate?: MutateCallback) {
        super([]);

        let items: IHeaderCommandBarItem[] = [];
        items = items.concat(staticItems);

        this._contributedItems = [];

        contributions.forEach((contribution: IHeaderCommandContribution) => {
            const actionProvider = new ContributedPivotBarActionProvider({
                context: pageContext,
                targetId: contribution.contributionId,
                getContext: contribution.getContributionContext
            });
            let array = new ContributedItemWithIdArray(actionProvider, x => x, x => x.key);
            this._contributedItems.push(array);
            array.subscribe((ev, action) => {
                this.value = this._getItems();
            });
        });

        this._staticItems = staticItems;
        this._divider = divider;
        this._order = order;
        this._mutate = mutate;
        this.value = this._getItems();
    }

    private _getItems(): IHeaderCommandBarItem[] {
        let allItems: IHeaderCommandBarItem[] = [];

        if (this._order === ItemOrdering.ContributedFirst) {
            this._contributedItems.forEach((array: ContributedItemArray<IPivotBarAction, IPivotBarAction>) => {
                allItems = allItems.concat(array.value.map(this._convert));
            });
            if (allItems.length > 0 && this._divider) {
                allItems = allItems.concat(this._divider);
            }
        }

        allItems = allItems.concat(this._staticItems);
        
        if (this._order === ItemOrdering.StaticFirst) {
            let dividerAdded: boolean = false;
            this._contributedItems.forEach((array: ContributedItemArray<IPivotBarAction, IPivotBarAction>) => {
                if (this._divider && !dividerAdded && array.value.length > 0) {
                    allItems = allItems.concat(this._divider);
                    dividerAdded = true;
                }
                allItems = allItems.concat(array.value.map(this._convert));
            });
        }
        return allItems;
    }

    private _convert = (action: IPivotBarAction): IHeaderCommandBarItem => {
        let item = {
            id: action.key,
            text: action.name,
            iconProps: action.iconProps,
            isPrimary: false,
            important: false,
            disabled: action.disabled,
            href: action.href,
            onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => action.onClick && action.onClick(event),
            itemType: action.separator ? MenuItemType.Divider : MenuItemType.Normal,
        } as IHeaderCommandBarItem;

        if (action.children) {
            item.subMenuProps = {} as IMenuProps;
            item.subMenuProps.id = action.key + "-submenu";
            let children: IMenuItem[] = [];
            action.children.forEach((value: IPivotBarAction) => {
                children.push(this._convertChildAction(value));
            });
            item.subMenuProps.items = children;
        }

        if (this._mutate) {
            item = this._mutate(item);
        }
        return item;
    }

    private _convertChildAction = (action: IPivotBarAction): IMenuItem => {
        let menuItem: IMenuItem = {
            id: action.key,
            text: action.name,
            iconProps: action.iconProps,
            disabled: action.disabled,
            href: action.href,
            onActivate: (menuItem: IMenuItem, event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => action.onClick && action.onClick(event),
            itemType: action.separator ? MenuItemType.Divider : MenuItemType.Normal
        };

        if (action.children)
        {
            menuItem.subMenuProps = {
                id: action.key + "submenu",
                items: []
            }
            let subMenuItems: IMenuItem[] = [];
            action.children.forEach((childAction: IPivotBarAction) => {
                subMenuItems.push(this._convertChildAction(childAction));
            });
            menuItem.subMenuProps.items = subMenuItems;
        }
        
        if (this._mutate) {
            menuItem = this._mutate(menuItem);
        }
        return menuItem;
    }
}
